import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

export default function AdminEngagement() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get stats
      const statsRes = await fetch('/api/engagement/admin/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      // Get users list
      const usersRes = await fetch('/api/engagement/admin/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData);
      }
    } catch (err) {
      console.error('Failed to fetch engagement stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading engagement data...</div>;
  if (!stats) return <div className="text-center py-8">Unable to load engagement data</div>;

  const tierColors = {
    Curious: 'bg-gray-200 text-gray-700',
    Interested: 'bg-blue-200 text-blue-700',
    Engaged: 'bg-green-200 text-green-700',
    Advocate: 'bg-purple-200 text-purple-700',
    Champion: 'bg-yellow-200 text-yellow-800'
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">📊 User Engagement Dashboard</h1>
      
      <div className="border-b mb-6">
        <nav className="flex flex-wrap gap-4">
          <button 
            onClick={() => setActiveTab('overview')}
            className={`pb-2 px-1 ${activeTab === 'overview' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500'}`}
          >
            Overview
          </button>
          <button 
            onClick={() => setActiveTab('users')}
            className={`pb-2 px-1 ${activeTab === 'users' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500'}`}
          >
            User List
          </button>
          <button 
            onClick={() => setActiveTab('modals')}
            className={`pb-2 px-1 ${activeTab === 'modals' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500'}`}
          >
            Modal Completion
          </button>
        </nav>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Total Users Tracked</p>
              <p className="text-2xl font-bold">{stats.totalUsers}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Avg Engagement Score</p>
              <p className="text-2xl font-bold">{stats.averageEngagementScore}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Newsletter Subscribers</p>
              <p className="text-2xl font-bold">{stats.totalNewsletterSubs}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Most Popular Modal</p>
              <p className="text-2xl font-bold capitalize">{stats.mostPopularModal?.replace('_', ' ') || 'N/A'}</p>
            </div>
          </div>

          {/* Engagement Tiers */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Engagement Tiers</h2>
            <div className="space-y-3">
              {Object.entries(stats.tiers).map(([tier, count]) => (
                <div key={tier} className="flex items-center">
                  <span className={`px-3 py-1 rounded-full text-sm ${tierColors[tier] || 'bg-gray-200'}`}>
                    {tier}
                  </span>
                  <span className="ml-3 text-gray-600">{count} users</span>
                  <div className="ml-4 flex-1 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all"
                      style={{ width: `${(count / stats.totalUsers) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Insights */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Quick Insights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Most Engaged User</p>
                <p className="font-medium">
                  {users.length > 0 && users[0]?.userId?.name 
                    ? `${users[0].userId.name} (Score: ${users[0].engagementScore})` 
                    : 'N/A'}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Highest Interest</p>
                <p className="font-medium">
                  {stats.mostPopularModal?.replace('_', ' ') || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modals Completed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Newsletter</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interests</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((record) => (
                <tr key={record._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm">
                    <div className="font-medium">{record.userId?.name || 'Unknown'}</div>
                    <div className="text-gray-500 text-xs">{record.userId?.email}</div>
                  </td>
                  <td className="px-6 py-4 text-sm capitalize">{record.userId?.role || 'N/A'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${tierColors[record.engagementTier] || 'bg-gray-200'}`}>
                      {record.engagementTier}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">{record.engagementScore}</td>
                  <td className="px-6 py-4 text-sm">
                    {Object.keys(record.modals).filter(m => record.modals[m]?.completed).length}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {record.newsletters.subscribed ? '✅' : '❌'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {Object.entries(record.interests)
                      .filter(([_, data]) => data.interested)
                      .map(([key]) => key.replace('_', ' '))
                      .join(', ') || 'None'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'modals' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Modal Completion Rates</h2>
          <div className="space-y-4">
            {Object.entries(stats.modalCompletions).map(([modal, count]) => (
              <div key={modal}>
                <div className="flex justify-between text-sm">
                  <span className="capitalize">{modal.replace('_', ' ')}</span>
                  <span className="text-gray-500">{count} completions</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{ width: `${(count / stats.totalUsers) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
